import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Features = ({ language }) => {
  const features = [
    {
      icon: FiIcons.FiCalendar,
      titleZh: '可以記錄哪些紀念日？',
      titleEn: 'Which Anniversaries Can You Track?',
      descZh: '初次相遇、第一次約會、開始交往、求婚、結婚... 所有重要的日子都能在這裡被好好珍藏。',
      descEn: 'From first meeting to wedding day, all your important milestones can be beautifully treasured here.',
      rotate: 'rotate-1'
    },
    {
      icon: FiIcons.FiImage,
      titleZh: '如何生成精美分享卡？',
      titleEn: 'How Can You Generate Share Cards?',
      descZh: '一鍵匯出質感漸層分享卡片，完美支援 IG 貼文與限時動態比例，讓你的每個重要日子都能在社群上閃閃發亮！✨',
      descEn: 'Export elegant gradient cards with one click. Perfectly sized for IG posts and stories to make your special memories shine! ✨',
      rotate: '-rotate-1'
    },
    {
      icon: FiIcons.FiAward,
      titleZh: '如何解鎖愛情成就？',
      titleEn: 'How Do You Unlock Love Achievements?',
      descZh: '隨著在一起的天數增加，你們會解鎖各種可愛的「愛情徽章」，就像遊戲破關一樣充滿成就感！',
      descEn: 'Unlock cute love badges as your days together grow. Treat your relationship like a fun game!',
      rotate: 'rotate-2'
    },
    {
      icon: FiIcons.FiBell,
      titleZh: '如何追蹤浪漫里程碑？',
      titleEn: 'How Can You Track Romantic Milestones?',
      descZh: '100天、520天、1314天... 我們會幫你算好每個特別的里程碑，讓你們不錯過任何慶祝的理由。',
      descEn: '100 days, 520 days, 1 year... We calculate all special milestones so you never miss a celebration.',
      rotate: '-rotate-2'
    },
    {
      icon: FiIcons.FiClock,
      titleZh: '如何精確計算相隔天數？',
      titleEn: 'How Can You Calculate Exact Days?',
      descZh: '不管是算「在一起幾天」還是期待「還要幾天才見面」，都能精準換算成年、月、日。',
      descEn: 'Count the exact years, months, and days you\'ve been together or the days left until you meet again.',
      rotate: 'rotate-1'
    },
    {
      icon: FiIcons.FiBriefcase,
      titleZh: '如何計算工作日？',
      titleEn: 'How Can You Calculate Workdays?',
      descZh: '除了談戀愛，也照顧你的日常！專屬的日期計算器可以幫你自動扣除週末，精算工作天數。',
      descEn: 'Not just for love! Our date calculator helps you exclude weekends to count business days accurately.',
      rotate: '-rotate-1'
    }
  ];

  return (
    <article
      className="max-w-4xl mx-auto p-4 md:p-8"
      itemScope
      itemType="https://schema.org/Article"
    >
      {/* 標題區 */}
      <div className="text-center mb-16 relative">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1
            className="text-4xl md:text-5xl font-black mb-6 text-gray-800 dark:text-white"
            itemProp="headline"
          >
            <span className="marker-highlight">
              {language === 'zh' ? '我們能為你做什麼？' : 'What Can We Do?'}
            </span>
          </h1>
          <p
            className="text-lg text-gray-600 dark:text-gray-300 font-medium max-w-2xl mx-auto"
            itemProp="description"
          >
            {language === 'zh' ? '不只是一個計算器，更是你們愛情旅程的手帳本。來看看有哪些可愛的功能吧！' : 'More than a calculator, it\'s a scrapbook for your love journey. Let\'s explore the cute features!'}
          </p>
          <p className="mt-4 text-sm font-medium text-gray-500 dark:text-gray-400">
            {language === 'zh' ? (
              <>
                發布日期：
                <time dateTime="2024-05-21" itemProp="datePublished">2024 年 5 月 21 日</time>
                <span aria-hidden="true"> · </span>
                更新日期：
                <time dateTime="2025-03-08" itemProp="dateModified">2025 年 3 月 8 日</time>
              </>
            ) : (
              <>
                Published: <time dateTime="2024-05-21" itemProp="datePublished">May 21, 2024</time>
                <span aria-hidden="true"> · </span>
                Updated: <time dateTime="2025-03-08" itemProp="dateModified">March 8, 2025</time>
              </>
            )}
          </p>
        </motion.div>
      </div>

      {/* 手繪風功能卡片網格 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            whileHover={{ scale: 1.05, rotate: 0 }}
            className={`hand-drawn-box bg-white dark:bg-slate-800 p-6 md:p-8 transform ${feature.rotate} transition-transform duration-300 relative z-10`}
          >
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-500 flex items-center justify-center text-2xl mr-4 shrink-0 shadow-sm border border-pink-200 dark:border-pink-800/50">
                <SafeIcon icon={feature.icon} />
              </div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white mt-2">
                {language === 'zh' ? feature.titleZh : feature.titleEn}
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
              {language === 'zh' ? feature.descZh : feature.descEn}
            </p>
          </motion.div>
        ))}
      </div>

      {/* 拍立得風格：圖片生成展示 */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16 tape-top bg-gradient-to-br from-pink-50 to-rose-50 dark:from-slate-800 dark:to-slate-900 p-8 md:p-10 rounded-3xl border-2 border-dashed border-pink-200 dark:border-slate-700 shadow-md"
      >
        <div className="flex flex-col md:flex-row items-center gap-10">
          <div className="flex-1 space-y-4">
            <h2 className="text-2xl md:text-3xl font-black text-pink-600 dark:text-pink-400 flex items-center gap-2 mb-6">
              <SafeIcon icon={FiIcons.FiCamera} />
              {language === 'zh' ? '如何製作專屬紀念日小卡？' : 'How Do You Create a Personal Anniversary Card?'}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 font-medium text-lg leading-relaxed">
              {language === 'zh'
                ? '系統會根據你選擇的卡片主題色，自動生成帶有毛玻璃透視質感的精美分享圖。不僅能完美呈現你的標題與專屬備註，更能無縫結合你的社群排版。'
                : 'The system automatically generates a beautiful glassmorphism share card based on your chosen theme. It perfectly presents your titles and notes while seamlessly fitting your social media feed.'}
            </p>
            <ul className="space-y-2 mt-4 text-gray-600 dark:text-gray-400 font-medium">
              <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /> {language === 'zh' ? '高畫質 PNG 匯出' : 'High-quality PNG export'}</li>
              <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /> {language === 'zh' ? '一鍵切換 IG 限動與貼文比例' : 'One-click switch for IG Stories & Posts'}</li>
              <li className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCheck} className="text-green-500" /> {language === 'zh' ? '質感毛玻璃 (Glassmorphism) 設計' : 'Elegant Glassmorphism design'}</li>
            </ul>
          </div>
          <div className="shrink-0 relative transform rotate-3 hover:rotate-0 transition-transform duration-300">
            <div className="bg-white p-3 pb-12 rounded-sm shadow-xl border border-gray-100 max-w-[240px]">
              <div className="aspect-[4/5] bg-gradient-to-br from-pink-300 to-purple-400 rounded-sm flex flex-col items-center justify-center text-white">
                <span className="text-5xl font-black mb-2">520</span>
                <span className="font-bold tracking-widest text-sm">DAYS</span>
              </div>
              <div className="absolute bottom-4 left-0 w-full text-center font-caveat text-gray-500 font-bold text-sm transform -rotate-2">
                Our Memories ♡
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 便利貼風格：即將推出 */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="max-w-xl mx-auto sticky-note p-8 md:p-10 transform -rotate-1 hover:rotate-0 transition-transform duration-300"
      >
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-12 h-12 bg-red-400 rounded-full shadow-sm flex items-center justify-center text-white z-10 border-4 border-white/20">
          <SafeIcon icon={FiIcons.FiPaperclip} className="w-5 h-5" />
        </div>

        <h2 className="text-xl font-black text-amber-900 dark:text-amber-100 mb-6 text-center mt-2 border-b-2 border-amber-900/10 pb-4">
          {language === 'zh' ? '📝 DaysMatter 接下來會推出什麼？' : '📝 What Is Coming Next to DaysMatter?'}
        </h2>

        <ul className="space-y-4 text-amber-900/80 dark:text-amber-200/80 font-medium text-lg">
          <li className="flex items-start gap-3">
            <span className="text-xl leading-none">☐</span>
            <span>{language === 'zh' ? '信箱通知：紀念日快到了自動提醒你！' : 'Email Reminders for upcoming anniversaries!'}</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-xl leading-none">☐</span>
            <span>{language === 'zh' ? '照片時間軸：上傳照片記錄每一刻。' : 'Photo Timeline to upload memories.'}</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-xl leading-none">☐</span>
            <span>{language === 'zh' ? '情侶綁定：兩個人一起共同編輯手帳。' : 'Couple Linking to edit scrapbook together.'}</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-xl leading-none">☐</span>
            <span>{language === 'zh' ? '專屬桌面小工具 (Widgets)。' : 'Exclusive Desktop Widgets.'}</span>
          </li>
        </ul>
      </motion.div>
    </article>
  );
};

export default Features;